export class ApiError extends Error {
  status: number
  body: unknown

  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

export async function api_json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {})
  if (!(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(path, {
    ...init,
    headers,
    credentials: 'include'
  })

  const text = await res.text()
  const body = text ? safe_json_parse(text) : null

  if (!res.ok) {
    const erro = new ApiError(`API error (${res.status})`, res.status, body)
    notify_observers(path, erro)
    throw erro
  }
  return body as T
}

/**
 * Observador de falha de API.
 *
 * Existe para o 401 ser tratado UMA vez, no lugar por onde toda chamada passa —
 * e não em cada `catch` de cada tela, que é como uma sessão expirada virava
 * "lista vazia" em vez de "entre de novo". O `api.ts` não importa `auth.ts`
 * (seria ciclo): quem sabe o que fazer se registra no boot da SPA.
 */
export type ApiFailureObserver = (path: string, err: ApiError) => void

const observers: ApiFailureObserver[] = []

export function on_api_failure(observer: ApiFailureObserver): void {
  observers.push(observer)
}

function notify_observers(path: string, err: ApiError): void {
  for (const observer of observers) {
    try {
      observer(path, err)
    } catch {
      // Observador que quebra não pode engolir o erro original, que é o que a
      // tela precisa receber.
    }
  }
}

export function get_api_error_code(err: unknown): string {
  if (!(err instanceof ApiError)) return ''
  const body = err.body as any
  return String(body?.error?.code || body?.error || '').trim()
}

export function get_api_error_message(err: unknown, fallback = 'Falha ao processar a solicitação.'): string {
  if (!(err instanceof ApiError)) return fallback
  const body = err.body as any
  return String(body?.error?.message || body?.error || fallback).trim()
}

function safe_json_parse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { raw: text }
  }
}
